import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, TrendingUp, Zap, Clock, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import apiService from '../services/api';

export default function PayoutHistory() {
  const { t } = useTranslation();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await apiService.get('/investments/payouts/history');
      if (res.success) setPayouts(res.data || []);
    } catch (err) {
      console.error('Error fetching payout history:', err);
    } finally {
      setLoading(false);
    }
  };

  const displayedPayouts = showAll ? payouts : payouts.slice(0, 5);

  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-slate-900/60 via-slate-900/30 to-indigo-900/10 border border-white/5 p-12 text-center">
        <Loader2 size={32} className="animate-spin text-indigo-500 mx-auto mb-4" />
        <p className="text-slate-500 font-black uppercase tracking-[0.3em] text-[10px]">Chargement historique...</p>
      </div>
    );
  }

  if (payouts.length === 0) return null;

  return (
    <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-slate-900/60 via-slate-900/30 to-indigo-900/10 border border-white/5 shadow-[0_20px_60px_-16px_rgba(2,6,23,0.5)]">
      <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-[100px] pointer-events-none" />
      
      <div className="p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20">
            <TrendingUp size={18} className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-black font-outfit uppercase tracking-tight text-white">{t('investments.payout_history') || 'Historique des Gains'}</h2>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-0.5">{payouts.length} {t('investments.payouts') || 'paiements'}</p>
          </div>
        </div>

        <div className="space-y-3">
          {displayedPayouts.map((p, i) => {
            const date = new Date(p.paid_at || p.created_at);
            return (
              <div key={p.id} className="group flex items-center justify-between p-5 rounded-2xl bg-slate-950/40 border border-white/5 hover:border-emerald-500/20 transition-all duration-300 hover:-translate-y-0.5">
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center">
                      <Zap size={18} className="text-emerald-400" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-slate-950 flex items-center justify-center">
                      <CheckCircle size={8} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-black text-white">{t('investments.payout') || 'Paiement'} — {p.plan_name || p.investment_plan_name || `Plan #${p.investment_id}`}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                        <Calendar size={10} />
                        {date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500">
                        <Clock size={10} />
                        {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-emerald-400 font-outfit tabular-nums">+{parseFloat(p.amount || p.payout_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</p>
                  <p className="text-[9px] font-black text-slate-600 uppercase tracking-widest mt-0.5">{p.asset_symbol || 'USDT'}</p>
                </div>
              </div>
            );
          })}
        </div>

        {payouts.length > 5 && (
          <button onClick={() => setShowAll(!showAll)}
            className="mt-6 w-full py-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-indigo-500/20 text-[10px] font-black text-slate-400 uppercase tracking-widest transition-all flex items-center justify-center gap-2 hover:text-white">
            {showAll ? t('investments.show_less') || 'Voir moins' : `${t('investments.view_all') || 'Voir tout'} (${payouts.length})`}
            <ArrowRight size={14} className={`transition-transform ${showAll ? 'rotate-90' : ''}`} />
          </button>
        )}
      </div>
    </div>
  );
}